/**
 * GRAPH SURGERY — the handful of edits that have to be right, kept away from the rendering.
 *
 * The compiler follows exactly ONE outgoing edge from every node except a branch, and on a
 * branch it follows one edge per outlet. A canvas that lets you draw a second wire off a
 * "say" node is therefore a canvas that silently throws half your flow away at save time.
 * Every function here exists to make an impossible-to-compile shape impossible to DRAW:
 *
 *   connect()       one wire per outlet. Reconnecting an outlet replaces the old wire.
 *   removeNodes()   deleting a step heals the wire through it instead of orphaning the tail.
 *   insertOnEdge()  dropping a node on a wire splices it in.
 *   renameCase()    a branch outlet is its case value, so renaming one moves its wire too.
 *
 * None of these mutate. Every one returns a new graph, because undo is a stack of graphs and
 * a stack of aliases to the same object is not an undo stack.
 */
import type { Edge, Node, XYPosition } from '@xyflow/react';

import { NODE_SPECS, newEdgeId, newNodeId } from './model.ts';
import type { NdData, NdKind } from './model.ts';

export type NdNode = Node<NdData, NdKind>;
export type NdEdge = Edge;
export type NdGraph = { nodes: NdNode[]; edges: NdEdge[] };

export const EMPTY_GRAPH: NdGraph = { nodes: [], edges: [] };

// ------------------------------------------------------------------------------ reading

export function nodeById(graph: NdGraph, id: string): NdNode | undefined {
  return graph.nodes.find((n) => n.id === id);
}

export function kindOf(node: NdNode | undefined): NdKind | undefined {
  return node?.type as NdKind | undefined;
}

export function outgoing(graph: NdGraph, id: string): NdEdge[] {
  return graph.edges.filter((e) => e.source === id);
}

export function incoming(graph: NdGraph, id: string): NdEdge[] {
  return graph.edges.filter((e) => e.target === id);
}

export function casesOf(node: NdNode): string[] {
  const raw = node.data.cases;
  return Array.isArray(raw) ? raw.filter((c): c is string => typeof c === 'string') : [];
}

/** True when this outlet already has a wire on it. The card dims the dot rather than lying. */
export function outletTaken(graph: NdGraph, id: string, handle: string | null): boolean {
  return graph.edges.some((e) => e.source === id && (e.sourceHandle ?? null) === handle);
}

/** Whether a new wire may legally leave this node at all. */
export function hasFreeOutlet(graph: NdGraph, node: NdNode): boolean {
  const spec = NODE_SPECS[node.type as NdKind];
  if (!spec?.hasOut) return false;
  if (!spec.multiOut) return !outletTaken(graph, node.id, null);
  return casesOf(node).some((c) => !outletTaken(graph, node.id, c));
}

// ------------------------------------------------------------------------------ writing

/**
 * Add a wire. An outlet holds one wire, so connecting an outlet that is already wired
 * REPLACES it — the alternative is a second edge the compiler will never walk, drawn in a
 * colour that says it will.
 */
export function connect(
  graph: NdGraph,
  params: { source: string; target: string; sourceHandle?: string | null },
): NdGraph {
  const { source, target } = params;
  const handle = params.sourceHandle ?? null;
  if (source === target) return graph;

  const src = nodeById(graph, source);
  const dst = nodeById(graph, target);
  if (!src || !dst) return graph;
  if (!NODE_SPECS[src.type as NdKind]?.hasOut) return graph;
  if (!NODE_SPECS[dst.type as NdKind]?.hasIn) return graph;

  const kept = graph.edges.filter((e) => !(e.source === source && (e.sourceHandle ?? null) === handle));
  const edge: NdEdge = {
    id: newEdgeId(source, target, handle),
    source,
    target,
    ...(handle ? { sourceHandle: handle, label: handle } : {}),
  };
  return { nodes: graph.nodes, edges: [...kept, edge] };
}

/**
 * Delete steps and HEAL. Removing a "say" from the middle of a flow should leave the flow
 * working with one fewer sentence, not leave everything after it unreachable — which is what
 * a plain filter does, and it is invisible until the compiler warns about six orphans.
 *
 * A branch is the exception it has to be: it has several tails and a healed wire can only go
 * to one, so its first outlet wins and the caller is told (the editor raises a toast).
 */
export function removeNodes(graph: NdGraph, ids: string[]): { graph: NdGraph; healed: number; dropped: number } {
  const doomed = new Set(ids);
  if (doomed.size === 0) return { graph, healed: 0, dropped: 0 };

  let healed = 0;
  let dropped = 0;
  let edges = graph.edges;

  for (const id of doomed) {
    const ins = edges.filter((e) => e.target === id && !doomed.has(e.source));
    const outs = edges.filter((e) => e.source === id && !doomed.has(e.target));
    const survivor = outs[0];

    edges = edges.filter((e) => e.source !== id && e.target !== id);
    if (outs.length > 1) dropped += outs.length - 1;

    if (survivor) {
      for (const inEdge of ins) {
        const handle = inEdge.sourceHandle ?? null;
        // Do not create a second wire on an outlet that the heal would double up.
        const clash = edges.some((e) => e.source === inEdge.source && (e.sourceHandle ?? null) === handle);
        if (clash) continue;
        edges = [
          ...edges,
          {
            id: newEdgeId(inEdge.source, survivor.target, handle),
            source: inEdge.source,
            target: survivor.target,
            ...(handle ? { sourceHandle: handle, label: handle } : {}),
          },
        ];
        healed += 1;
      }
    }
  }

  return {
    graph: { nodes: graph.nodes.filter((n) => !doomed.has(n.id)), edges },
    healed,
    dropped,
  };
}

/** Build a node of a kind at a position, with the defaults its inspector expects. */
export function makeNode(graph: NdGraph, kind: NdKind, position: XYPosition): NdNode {
  return {
    id: newNodeId(kind, graph.nodes.map((n) => n.id)),
    type: kind,
    position,
    data: NODE_SPECS[kind].defaults(),
  };
}

/** Drop a node onto empty canvas. Nothing is wired; the operator draws the wire. */
export function addNode(graph: NdGraph, node: NdNode): NdGraph {
  return { nodes: [...graph.nodes, node], edges: graph.edges };
}

/**
 * Drop a node ON a wire and splice it in: A→B becomes A→new→B. The case label rides with
 * the first half, because it belonged to A's outlet and not to the wire's destination.
 */
export function insertOnEdge(graph: NdGraph, node: NdNode, edgeId: string): NdGraph {
  const edge = graph.edges.find((e) => e.id === edgeId);
  if (!edge) return addNode(graph, node);
  const spec = NODE_SPECS[node.type as NdKind];
  if (!spec.hasIn || !spec.hasOut) return addNode(graph, node);

  const handle = edge.sourceHandle ?? null;
  const rest = graph.edges.filter((e) => e.id !== edgeId);
  return {
    nodes: [...graph.nodes, node],
    edges: [
      ...rest,
      {
        id: newEdgeId(edge.source, node.id, handle),
        source: edge.source,
        target: node.id,
        ...(handle ? { sourceHandle: handle, label: handle } : {}),
      },
      { id: newEdgeId(node.id, edge.target), source: node.id, target: edge.target },
    ],
  };
}

/** Drop a node with something selected: wire it on if the selection has a free outlet. */
export function appendAfter(graph: NdGraph, node: NdNode, afterId: string | null): NdGraph {
  const withNode = addNode(graph, node);
  if (!afterId) return withNode;
  const src = nodeById(graph, afterId);
  if (!src) return withNode;
  const spec = NODE_SPECS[src.type as NdKind];
  if (!spec?.hasOut) return withNode;
  if (!spec.multiOut) {
    if (outletTaken(graph, afterId, null)) return withNode;
    return connect(withNode, { source: afterId, target: node.id });
  }
  const free = casesOf(src).find((c) => !outletTaken(graph, afterId, c));
  if (!free) return withNode;
  return connect(withNode, { source: afterId, target: node.id, sourceHandle: free });
}

// ------------------------------------------------------------------------- node data edits

export function patchData(graph: NdGraph, id: string, patch: NdData): NdGraph {
  return {
    nodes: graph.nodes.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...patch } } : n)),
    edges: graph.edges,
  };
}

/**
 * A BRANCH OUTLET IS ITS CASE VALUE. That is not a convention, it is the compiler's contract
 * (`sourceHandle` becomes `on=value`), which is why renaming an outcome has to carry its wire
 * with it and why an empty name is refused here rather than at save time.
 */
export function renameCase(graph: NdGraph, id: string, from: string, to: string): NdGraph {
  const next = to.trim();
  if (!next || next === from) return graph;
  const node = nodeById(graph, id);
  if (!node) return graph;
  const cases = casesOf(node);
  if (cases.includes(next)) return graph;

  const nodes = graph.nodes.map((n) =>
    n.id === id ? { ...n, data: { ...n.data, cases: cases.map((c) => (c === from ? next : c)) } } : n,
  );
  const edges = graph.edges.map((e) =>
    e.source === id && (e.sourceHandle ?? null) === from
      ? { ...e, id: newEdgeId(id, e.target, next), sourceHandle: next, label: next }
      : e,
  );
  return { nodes, edges };
}

export function addCase(graph: NdGraph, id: string, value: string): NdGraph {
  const next = value.trim();
  if (!next) return graph;
  const node = nodeById(graph, id);
  if (!node) return graph;
  const cases = casesOf(node);
  if (cases.includes(next)) return graph;
  return patchData(graph, id, { cases: [...cases, next] });
}

export function removeCase(graph: NdGraph, id: string, value: string): NdGraph {
  const node = nodeById(graph, id);
  if (!node) return graph;
  const cases = casesOf(node).filter((c) => c !== value);
  return {
    nodes: graph.nodes.map((n) => (n.id === id ? { ...n, data: { ...n.data, cases } } : n)),
    edges: graph.edges.filter((e) => !(e.source === id && (e.sourceHandle ?? null) === value)),
  };
}

// ------------------------------------------------------------------------------ seeding

/**
 * A NEW FLOW IS NOT AN EMPTY SHEET. An empty canvas is a puzzle; an entry node with one
 * sentence under it is a thing you can change. This is the shape an owner sees first, and it
 * already compiles — the read-through reads as English on the first frame.
 */
export function seedGraph(): NdGraph {
  const entry: NdNode = { id: 'entry_1', type: 'entry', position: { x: 0, y: 0 }, data: {} };
  const say: NdNode = {
    id: 'say_1',
    type: 'say',
    position: { x: 0, y: 150 },
    data: { text: 'Thanks for calling. Our office is closed for the night.' },
  };
  const end: NdNode = { id: 'end_1', type: 'end', position: { x: 0, y: 320 }, data: {} };
  return {
    nodes: [entry, say, end],
    edges: [
      { id: newEdgeId(entry.id, say.id), source: entry.id, target: say.id },
      { id: newEdgeId(say.id, end.id), source: say.id, target: end.id },
    ],
  };
}

// ------------------------------------------------------------------------- wire ⇄ canvas

/**
 * The server stores a plain `{ nodes, edges }`; React Flow wants its own extra keys. These
 * two functions are the only place the two shapes meet, so a React-Flow-only field (measured
 * sizes, selection, drag state) can never reach the database and get saved as call flow.
 */
export function fromWire(raw: unknown): NdGraph {
  const graph = (raw ?? {}) as { nodes?: unknown; edges?: unknown };
  const nodes = Array.isArray(graph.nodes) ? graph.nodes : [];
  const edges = Array.isArray(graph.edges) ? graph.edges : [];
  const known = new Set(Object.keys(NODE_SPECS));

  const clean: NdNode[] = [];
  for (const raw2 of nodes) {
    const n = raw2 as { id?: unknown; type?: unknown; position?: unknown; data?: unknown };
    if (typeof n.id !== 'string' || typeof n.type !== 'string' || !known.has(n.type)) continue;
    const pos = (n.position ?? {}) as { x?: unknown; y?: unknown };
    clean.push({
      id: n.id,
      type: n.type as NdKind,
      position: { x: typeof pos.x === 'number' ? pos.x : 0, y: typeof pos.y === 'number' ? pos.y : 0 },
      data: (n.data && typeof n.data === 'object' ? (n.data as NdData) : {}),
    });
  }

  const ids = new Set(clean.map((n) => n.id));
  const cleanEdges: NdEdge[] = [];
  for (const raw2 of edges) {
    const e = raw2 as { id?: unknown; source?: unknown; target?: unknown; sourceHandle?: unknown; label?: unknown };
    if (typeof e.source !== 'string' || typeof e.target !== 'string') continue;
    if (!ids.has(e.source) || !ids.has(e.target)) continue;
    const handle = typeof e.sourceHandle === 'string' && e.sourceHandle ? e.sourceHandle : null;
    cleanEdges.push({
      id: typeof e.id === 'string' && e.id ? e.id : newEdgeId(e.source, e.target, handle),
      source: e.source,
      target: e.target,
      ...(handle ? { sourceHandle: handle, label: handle } : {}),
    });
  }

  return { nodes: clean, edges: cleanEdges };
}

export function toWire(graph: NdGraph): {
  nodes: { id: string; type: string; position: { x: number; y: number }; data: Record<string, unknown> }[];
  edges: { id: string; source: string; target: string; sourceHandle?: string | null; label?: string }[];
} {
  return {
    nodes: graph.nodes.map((n) => ({
      id: n.id,
      type: String(n.type),
      position: { x: Math.round(n.position.x), y: Math.round(n.position.y) },
      // Strip the render-only keys the editor hangs on data (issues, warnings, selection).
      data: stripRenderKeys(n.data),
    })),
    edges: graph.edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      ...(e.sourceHandle ? { sourceHandle: e.sourceHandle, label: e.sourceHandle } : {}),
    })),
  };
}

const RENDER_ONLY = new Set(['issues', 'warnings', 'selected', 'dragging']);

function stripRenderKeys(data: NdData): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(data)) {
    if (RENDER_ONLY.has(k)) continue;
    out[k] = v;
  }
  return out;
}

/** Two graphs are the same flow if the saved shape is the same. Positions count — a moved
 *  node is an unsaved change, and telling somebody their layout is safe when it is not is
 *  the kind of small lie that costs a night's work. */
export function sameGraph(a: NdGraph, b: NdGraph): boolean {
  return JSON.stringify(toWire(a)) === JSON.stringify(toWire(b));
}

// ------------------------------------------------------------------------------ layout

/**
 * TIDY — put a flow on the canvas in a shape a person can read without zooming out.
 *
 * Every starter arrived as ONE COLUMN: x pinned at 360, y stepping 132 a row. Eight steps is
 * 1,034px tall, eleven is 1,166, and the canvas is about 640 high. So "Fit" had to zoom to
 * 0.55 to get it on screen, which put the body text at seven pixels — on screen, and
 * unreadable, which is the same as not on screen. The one thing the starter list promises is
 * "press it and read it".
 *
 * Columns rather than rows, because the node handles decide this and not taste: a step takes
 * its wire in at the TOP and hands it out at the BOTTOM. Laid out left to right, every edge
 * would leave the bottom of one card and swing back up into the top of the next. Down a
 * column the wire runs the short way, exactly as the handles intend, and the only sweep in
 * the drawing is the one wrap from the foot of a column to the head of the next — which is
 * the same gesture as the return at the end of a line of text, and is drawn, not implied.
 *
 * Columns are BALANCED, not filled: nine steps is 5+4, not 5+4 by accident of overflow, and
 * eleven is 4+4+3 rather than 5+5+1 with one card marooned in its own column. Anything up to
 * COL_ROWS stays a single plain spine, because a four-step flow read top to bottom needs no
 * cleverness and gets none.
 *
 * A BRANCH is the one place the drawing must fan out: each case takes its own column, one row
 * below the branch, left to right in the order the outlets are listed. That is the shape the
 * flow actually has, so it is the shape it is drawn in.
 *
 * Deterministic on purpose. These positions are SAVED — the same flow must tidy to the same
 * picture every time, or "Saved" starts lying about what changed.
 */
const NODE_W = 264;
const COL_PITCH_X = NODE_W + 76;
const ROW_PITCH_Y = 132;
const ORIGIN_X = 360;
const ORIGIN_Y = 40;

/**
 * Rows before a straight run wraps. Four is not a round number, it is the one that makes the
 * worst flow in the set fit: "Pay a bill" is a five-step run into a branch with two three-step
 * cases, and at five rows the run fills a column on its own, the cases hang below it, and the
 * drawing is eight rows deep — 1,056px, worse than the height it was meant to fix. At four the
 * run wraps, the branch lands two rows down, and the whole thing is three columns by five.
 */
const COL_ROWS = 4;

export function tidy(graph: NdGraph): NdGraph {
  if (graph.nodes.length === 0) return graph;

  const start = entryNode(graph);
  if (!start) return graph;

  const placed = new Map<string, { col: number; row: number }>();
  let nextFreeCol = 0;

  /** Runs are laid out whole, so the column count is known before the first card is placed. */
  const runFrom = (id: string): string[] => {
    const run: string[] = [];
    let cursor: string | undefined = id;
    while (cursor && !placed.has(cursor) && !run.includes(cursor)) {
      run.push(cursor);
      const next = outgoing(graph, cursor);
      // A branch ends the run: it is the last card in it, and its cases start new columns.
      if (next.length !== 1) break;
      cursor = next[0]!.target;
    }
    return run;
  };

  const layRun = (ids: string[], firstCol: number, firstRow: number): void => {
    // Balance rather than fill, so no card is left alone at the top of a column of one.
    const cols = bestColumnCount(ids.length);
    const perCol = Math.ceil(ids.length / cols);
    ids.forEach((id, i) => {
      const col = firstCol + Math.floor(i / perCol);
      const row = (i % perCol) + (i < perCol ? firstRow : 0);
      placed.set(id, { col, row });
      nextFreeCol = Math.max(nextFreeCol, col + 1);
    });
  };

  const walk = (id: string, col: number, row: number): void => {
    const run = runFrom(id);
    if (run.length === 0) return;
    layRun(run, col, row);

    const tail = run[run.length - 1]!;
    const outs = outgoing(graph, tail);
    if (outs.length < 2) {
      // A run stops at a branch or at the end. One successor already means it was consumed
      // by runFrom() unless it was already placed — a join. Leave joins where they are.
      return;
    }
    /*
     * The first case carries straight on down the branch's own column and the rest fan out to
     * its right. Giving every case a fresh column is tidier on paper and wider on screen — it
     * spends a whole column to say "this one is no different from the others", when the shape
     * a reader is looking for is the main line continuing and the alternatives leaving it.
     */
    const tailAt = placed.get(tail)!;
    let firstCase = true;
    for (const edge of outs) {
      if (placed.has(edge.target)) continue;
      walk(edge.target, firstCase ? tailAt.col : nextFreeCol, tailAt.row + 1);
      firstCase = false;
    }
  };

  walk(start.id, 0, 0);

  return {
    ...graph,
    nodes: graph.nodes.map((n) => {
      const at = placed.get(n.id);
      if (!at) return n;
      return {
        ...n,
        position: { x: ORIGIN_X + at.col * COL_PITCH_X, y: ORIGIN_Y + at.row * ROW_PITCH_Y },
      };
    }),
  };
}

/**
 * How many columns to break a straight run into.
 *
 * Dividing by a fixed rows-per-column is the obvious rule and it is the wrong one, because
 * the thing being optimised is not the row count, it is how big the drawing can be drawn. A
 * run of nine at four rows is three columns — 944 by 396 — and on a canvas half as wide again
 * as it is tall that is a wide, shallow strip that runs out of WIDTH while half the height
 * goes unused, so the fit shrinks the type to obey a constraint that only exists in one axis.
 *
 * So try every split and keep the one that can be drawn largest against a reference canvas.
 * The reference is a constant and not the live viewport on purpose: these positions are SAVED,
 * and a layout that depended on the window would put a different picture in the database
 * depending on who opened it and how wide their browser happened to be.
 *
 * A run that fits in one column stays in one column. Four steps read top to bottom need no
 * cleverness, and a rule that splits them anyway is a rule that has forgotten what it is for.
 */
/*
 * MEASURED, not assumed. The canvas is not the window and it is not the content area — the
 * palette on the left and the inspector on the right take about 59% of it. On the deployed
 * console:
 *
 *     window 1440×900  →  canvas 590×600
 *     window 1600×1000 →  canvas 742×694
 *     window 1920×1080 →  canvas 1062×792
 *
 * The first guess here was 1200×750, which is nearly the whole content area, and every
 * arrangement it chose was too wide to draw — the fit then shrank the type to obey a width
 * that was never there. The reference is the SMALLEST of the three on purpose: a layout
 * tuned to a big monitor is unreadable on a laptop, while one tuned to the laptop simply has
 * room to spare on the monitor.
 */
const REFERENCE_CANVAS_W = 590;
const REFERENCE_CANVAS_H = 600;

function bestColumnCount(count: number): number {
  if (count <= COL_ROWS) return 1;

  let best = 1;
  let bestScore = -1;
  for (let cols = 1; cols <= count; cols += 1) {
    const rows = Math.ceil(count / cols);
    const w = (cols - 1) * COL_PITCH_X + NODE_W;
    const h = (rows - 1) * ROW_PITCH_Y + ROW_PITCH_Y;
    /*
     * Deliberately NOT capped at 1. Capping is right for the drawing — nobody wants the cards
     * blown up past their design size — but it is wrong for the CHOICE, because it flattens
     * every comfortable arrangement to the same score and hands the decision to the tie-break.
     * That is not hypothetical: with the cap, a five-step run scored 1.0 as one tall column
     * and 1.0 as two short ones, the tie went to one column, and the branch hanging off the
     * end of it put the drawing eight rows deep again. Score the headroom honestly and the
     * shape with real room to spare wins.
     */
    const score = Math.min(REFERENCE_CANVAS_W / w, REFERENCE_CANVAS_H / h);
    // Strictly greater, so a genuine tie goes to the FEWER columns — closer to a plain spine.
    if (score > bestScore) {
      bestScore = score;
      best = cols;
    }
  }
  return best;
}

/** The card the call starts at: the declared entry, else the one nothing points into. */
function entryNode(graph: NdGraph): NdNode | undefined {
  const entry = graph.nodes.find((n) => kindOf(n) === 'entry');
  if (entry) return entry;
  const targets = new Set(graph.edges.map((e) => e.target));
  return graph.nodes.find((n) => !targets.has(n.id)) ?? graph.nodes[0];
}
